// Builder.tsx — экран «Конструктор анкет» (Ф6, write). Визуальный редактор
// разделов / вопросов / повторов / условий поверх write-API /api/surveys.
//
// Источник истины — ГИБРИД (см. src/api/routes/surveys.ts): редактор присылает
// полное желаемое состояние, сервер собирает SurveyJson → те же валидаторы §I11
// → syncSurveysToDb. Здесь правила движка отражены в UI: code анкеты неизменяем,
// удаление вопроса = архивация (soft-delete на сервере), версия растёт на сервере.
//
// Хвосты Ф6 (закрыты 2026-06-08):
//  - условия для вопросов ВНУТРИ повтора редактируются (predicatesFor считает
//    предшественников по той же модели порядка, что validateConditionRefs);
//  - МНОЖЕСТВЕННЫЕ условия на вопрос (массив, движок их И-объединяет —
//    next-question.ts isAllowed);
//  - drag-n-drop разделов/вопросов (нативный HTML5 DnD; кнопки ↑↓ оставлены как
//    доступная альтернатива).

import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../api/client';
import { ApiError } from '../api/client';
import type {
  ConditionOperator,
  QuestionType,
  SurveyListResponse,
  SurveyTreeResponse,
  SurveyWritePayload,
} from '../api/contract';
import { StateCard } from '../components/StateBlock';

// --- типы черновика (мутабельная копия дерева + ключи для React) ------------

interface DraftCondition {
  dependsOn: string;
  operator: ConditionOperator;
  value: string | number | boolean;
}
interface DraftQuestion {
  key: string;
  code: string;
  text: string;
  type: QuestionType;
  isRequired: boolean;
  options: string[];
  conditions: DraftCondition[];
}
interface DraftRepeater {
  key: string;
  code: string;
  title: string;
  countQuestion: string;
  questions: DraftQuestion[];
}
interface DraftSection {
  key: string;
  code: string;
  title: string;
  description: string;
  questions: DraftQuestion[];
  repeaters: DraftRepeater[];
}
interface Draft {
  id: string;
  code: string;
  title: string;
  description: string;
  isActive: boolean;
  version: number;
  sections: DraftSection[];
}

const TYPE_LABELS: Record<QuestionType, string> = {
  yes_no: 'да/нет',
  single_choice: 'один из списка',
  number: 'число',
  date: 'дата',
  time: 'время',
  text_short: 'короткий текст',
  text_long: 'длинный текст',
  text_short_autocomplete: 'текст с автодополнением',
};
const TYPE_ORDER: QuestionType[] = [
  'yes_no',
  'single_choice',
  'number',
  'date',
  'time',
  'text_short',
  'text_long',
  'text_short_autocomplete',
];
const OP_LABELS: Record<ConditionOperator, string> = {
  equals: '=',
  not_equals: '≠',
  in: 'в списке',
  not_in: 'не в списке',
};

function uid(): string {
  return crypto.randomUUID().replace(/-/g, '').slice(0, 8);
}

// --- конвертация дерево с сервера → черновик и обратно -----------------------

function treeToDraft(t: SurveyTreeResponse): Draft {
  const q = (qq: SurveyTreeResponse['sections'][number]['questions'][number]): DraftQuestion => ({
    key: qq.id,
    code: qq.code,
    text: qq.text,
    type: qq.type,
    isRequired: qq.isRequired,
    options: qq.options ?? [],
    conditions: qq.conditions.map((c) => ({
      dependsOn: c.dependsOn,
      operator: c.operator,
      // массивные значения (in/not_in) в простом UI не редактируем — сводим к
      // строке для отображения; round-trip их сохранит, если не трогать.
      value: Array.isArray(c.value) ? c.value.join(', ') : (c.value as string | number | boolean),
    })),
  });
  return {
    id: t.id,
    code: t.code,
    title: t.title,
    description: t.description ?? '',
    isActive: t.isActive,
    version: t.version,
    sections: t.sections.map((s) => ({
      key: s.id,
      code: s.code,
      title: s.title,
      description: s.description ?? '',
      questions: s.questions.map(q),
      repeaters: s.repeaters.map((r) => ({
        key: r.id,
        code: r.code,
        title: r.title,
        countQuestion: r.countQuestion,
        questions: r.questions.map(q),
      })),
    })),
  };
}

function draftToPayload(d: Draft): SurveyWritePayload {
  const q = (qq: DraftQuestion) => {
    const conds = qq.conditions.filter((c) => c.dependsOn);
    return {
      code: qq.code,
      text: qq.text.trim(),
      type: qq.type,
      isRequired: qq.isRequired,
      options: qq.type === 'single_choice' ? qq.options.filter((o) => o.trim()) : undefined,
      conditions: conds.length
        ? conds.map((c) => ({ dependsOn: c.dependsOn, operator: c.operator, value: c.value }))
        : undefined,
    };
  };
  return {
    code: d.code,
    title: d.title.trim(),
    description: d.description.trim() || undefined,
    isActive: d.isActive,
    sections: d.sections.map((s) => ({
      code: s.code,
      title: s.title.trim(),
      description: s.description.trim() || undefined,
      questions: s.questions.map(q),
      repeaters: s.repeaters.map((r) => ({
        code: r.code,
        title: r.title.trim(),
        countQuestion: r.countQuestion,
        questions: r.questions.map(q),
      })),
    })),
  };
}

/** Раздел по индексу (под noUncheckedIndexedAccess; индекс валиден при рендере). */
function secAt(d: Draft, i: number): DraftSection {
  const s = d.sections[i];
  if (!s) throw new Error(`section index ${i} out of range`);
  return s;
}
/** Повтор по индексу раздела/повтора. */
function repAt(d: Draft, si: number, ri: number): DraftRepeater {
  const r = secAt(d, si).repeaters[ri];
  if (!r) throw new Error(`repeater index ${ri} out of range`);
  return r;
}

function newQuestion(): DraftQuestion {
  return {
    key: crypto.randomUUID(),
    code: `q_${uid()}`,
    text: 'Новый вопрос',
    type: 'yes_no',
    isRequired: true,
    options: [],
    conditions: [],
  };
}

interface Predicate {
  code: string;
  text: string;
  type: QuestionType;
}

// Плоский список всех вопросов анкеты с позицией (sectionOrder, questionOrder),
// вычисленной ТОЧНО как на сервере (toSurveyJson + validateConditionRefs):
// вопросы раздела — 0..n-1; вопросы повтора — (sec.questions.length+ri)*1000+qi.
// Это даёт корректный набор «строго до» для любого вопроса, включая повторные.
function orderedQuestions(
  d: Draft,
): { key: string; code: string; text: string; type: QuestionType; so: number; qo: number }[] {
  const out: {
    key: string;
    code: string;
    text: string;
    type: QuestionType;
    so: number;
    qo: number;
  }[] = [];
  d.sections.forEach((sec, si) => {
    sec.questions.forEach((q, qi) => {
      out.push({ key: q.key, code: q.code, text: q.text, type: q.type, so: si, qo: qi });
    });
    sec.repeaters.forEach((r, ri) => {
      r.questions.forEach((q, qi) => {
        out.push({
          key: q.key,
          code: q.code,
          text: q.text,
          type: q.type,
          so: si,
          qo: (sec.questions.length + ri) * 1000 + qi,
        });
      });
    });
  });
  return out;
}

// Предикаты для условия вопроса `ownerKey`: все вопросы строго ДО него по
// (sectionOrder, questionOrder). Работает и для вопросов внутри повтора.
function predicatesFor(d: Draft, ownerKey: string): Predicate[] {
  const all = orderedQuestions(d);
  const owner = all.find((x) => x.key === ownerKey);
  if (!owner) return [];
  return all
    .filter(
      (x) => x.key !== ownerKey && (x.so < owner.so || (x.so === owner.so && x.qo < owner.qo)),
    )
    .map((x) => ({ code: x.code, text: x.text, type: x.type }));
}

// ============================================================
// Drag-n-drop: нативный HTML5. Тащим за ⠿-ручку, бросаем на строку.
// Зоны (нельзя перетаскивать между зонами — это сменило бы владельца/коды):
//   'sections'              — порядок разделов
//   'sq:<sectionKey>'       — вопросы раздела
//   'rq:<sectionKey>|<repK>'— вопросы повтора
// ============================================================

type DragEvt = React.DragEvent<HTMLElement>;
interface HandleProps {
  draggable: true;
  onDragStart: (e: DragEvt) => void;
  onDragEnd: () => void;
  style: React.CSSProperties;
  title: string;
}
interface ZoneProps {
  onDragEnter: (e: DragEvt) => void;
  onDragOver: (e: DragEvt) => void;
  onDrop: (e: DragEvt) => void;
  'data-over'?: string;
  'data-dragging'?: string;
}
interface DndApi {
  handle: (zone: string, key: string) => HandleProps;
  zone: (zone: string, key: string) => ZoneProps;
}
const DndContext = createContext<DndApi | null>(null);
function useDnd(): DndApi {
  const v = useContext(DndContext);
  if (!v) throw new Error('DndContext missing');
  return v;
}

function arrForZone(d: Draft, zone: string): { key: string }[] | null {
  if (zone === 'sections') return d.sections;
  if (zone.startsWith('sq:')) {
    const sk = zone.slice(3);
    return d.sections.find((s) => s.key === sk)?.questions ?? null;
  }
  if (zone.startsWith('rq:')) {
    const rest = zone.slice(3);
    const sep = rest.indexOf('|');
    const sk = rest.slice(0, sep);
    const rk = rest.slice(sep + 1);
    const sec = d.sections.find((s) => s.key === sk);
    return sec?.repeaters.find((r) => r.key === rk)?.questions ?? null;
  }
  return null;
}

// --- компонент --------------------------------------------------------------

export default function Builder() {
  const [list, setList] = useState<SurveyListResponse['surveys']>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [loadingTree, setLoadingTree] = useState(false);
  const [treeError, setTreeError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const reloadList = useCallback(async () => {
    try {
      const res = await api.surveys();
      setList(res.surveys);
      setListError(null);
      return res.surveys;
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'ошибка загрузки списка');
      return [];
    }
  }, []);

  useEffect(() => {
    reloadList().then((surveys) => {
      const first = surveys.find((s) => s.isActive) ?? surveys[0];
      if (first) setSelectedId(first.id);
    });
  }, [reloadList]);

  useEffect(() => {
    if (!selectedId) {
      setDraft(null);
      return;
    }
    let cancelled = false;
    setLoadingTree(true);
    setTreeError(null);
    setSaveMsg(null);
    api
      .surveyTree(selectedId)
      .then((t) => {
        if (!cancelled) setDraft(treeToDraft(t));
      })
      .catch((e) => {
        if (!cancelled) setTreeError(e instanceof Error ? e.message : 'ошибка загрузки анкеты');
      })
      .finally(() => {
        if (!cancelled) setLoadingTree(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  /** Иммутабельная мутация черновика через deep-clone. */
  const update = useCallback((mut: (d: Draft) => void) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = structuredClone(prev);
      mut(next);
      return next;
    });
    setSaveMsg(null);
  }, []);

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const updated = await api.saveSurvey(draft.id, draftToPayload(draft));
      setDraft(treeToDraft(updated));
      setSaveMsg(`Сохранено → v${updated.version}`);
      await reloadList();
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'не удалось сохранить';
      setSaveMsg(`Ошибка: ${msg}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section>
      <div className="ph">
        <h1>Конструктор анкет</h1>
        <button type="button" className="btn" onClick={() => setCreating(true)}>
          + Создать анкету
        </button>
      </div>
      <p className="sub">
        Визуальный редактор: разделы, вопросы, типы, условия показа и повторы. Без правки JSON
        руками.
      </p>

      {listError && <StateCard kind="error" message={listError} />}

      <div className="build-layout">
        {/* список анкет */}
        <div className="card surveys-list" style={{ padding: 12 }}>
          {list.map((s) => (
            <button
              type="button"
              key={s.id}
              className={`sitem${s.id === selectedId ? ' on' : ''}${s.isActive ? '' : ' archived'}`}
              style={{ display: 'block', width: '100%', textAlign: 'left' }}
              onClick={() => setSelectedId(s.id)}
            >
              <b>{s.title}</b>
              <small>
                {s.sectionCount} разд. · {s.questionCount} вопр. · v{s.version} ·{' '}
                {s.isActive ? 'active' : 'архив'}
              </small>
            </button>
          ))}
          <button
            type="button"
            className="sitem new"
            style={{ display: 'block', width: '100%' }}
            onClick={() => setCreating(true)}
          >
            + новая анкета
          </button>
        </div>

        {/* редактор */}
        <div>
          {creating && (
            <CreateForm
              onCancel={() => setCreating(false)}
              onCreated={async (id) => {
                setCreating(false);
                await reloadList();
                setSelectedId(id);
              }}
            />
          )}
          {loadingTree && <StateCard kind="loading" message="Загрузка анкеты…" />}
          {treeError && <StateCard kind="error" message={treeError} />}
          {!loadingTree && !treeError && !draft && !creating && (
            <StateCard kind="empty" message="Выберите анкету слева или создайте новую." />
          )}

          {draft && !creating && (
            <Editor
              draft={draft}
              update={update}
              editingKey={editingKey}
              setEditingKey={setEditingKey}
              saving={saving}
              saveMsg={saveMsg}
              onSave={save}
            />
          )}
        </div>

        {/* живой предпросмотр */}
        {draft && !creating && <Preview draft={draft} />}
      </div>
    </section>
  );
}

// ============================================================
// Создание анкеты
// ============================================================

function CreateForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (id: string) => void;
}) {
  const [code, setCode] = useState('');
  const [title, setTitle] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const t = await api.createSurvey({ code: code.trim(), title: title.trim() });
      onCreated(t.id);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'не удалось создать');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 16, marginBottom: 12 }}>
      <h3 style={{ marginTop: 0 }}>Новая анкета</h3>
      <div className="qform" style={{ border: 0, background: 'transparent', padding: 0 }}>
        <div className="frow">
          <div className="f1">
            <label>Название</label>
            <input
              type="text"
              value={title}
              placeholder="ТЕСЛАБ — вечерний отчёт"
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="f1">
            <label>code (стабильный ID, латиница/цифры/_)</label>
            <input
              type="text"
              value={code}
              placeholder="teslab_evening"
              onChange={(e) => setCode(e.target.value)}
            />
          </div>
        </div>
        {err && <p style={{ color: 'var(--red)', fontSize: 13 }}>{err}</p>}
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button type="button" className="btn" disabled={busy || !code || !title} onClick={submit}>
            {busy ? 'Создаю…' : 'Создать'}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel}>
            Отмена
          </button>
        </div>
        <p className="sub" style={{ marginTop: 8 }}>
          Создастся со стартовым разделом и вопросом — дальше редактируйте здесь.
        </p>
      </div>
    </div>
  );
}

// ============================================================
// Редактор анкеты
// ============================================================

function Editor({
  draft,
  update,
  editingKey,
  setEditingKey,
  saving,
  saveMsg,
  onSave,
}: {
  draft: Draft;
  update: (mut: (d: Draft) => void) => void;
  editingKey: string | null;
  setEditingKey: (k: string | null) => void;
  saving: boolean;
  saveMsg: string | null;
  onSave: () => void;
}) {
  // --- состояние drag-n-drop (живёт на уровне всего дерева) ------------------
  const [drag, setDrag] = useState<{ zone: string; key: string } | null>(null);
  const [over, setOver] = useState<string | null>(null);

  const reorder = (zone: string, toKey: string) => {
    if (!drag || drag.zone !== zone || drag.key === toKey) return;
    update((d) => {
      const arr = arrForZone(d, zone);
      if (!arr) return;
      const from = arr.findIndex((x) => x.key === drag.key);
      const to = arr.findIndex((x) => x.key === toKey);
      if (from < 0 || to < 0) return;
      const [item] = arr.splice(from, 1);
      if (item) arr.splice(to, 0, item);
    });
  };

  const dnd: DndApi = {
    handle: (zone, key) => ({
      draggable: true,
      onDragStart: (e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', key);
        setDrag({ zone, key });
      },
      onDragEnd: () => {
        setDrag(null);
        setOver(null);
      },
      style: { cursor: 'grab' },
      title: 'перетащите, чтобы изменить порядок',
    }),
    zone: (zone, key) => ({
      onDragEnter: (e) => {
        if (drag?.zone === zone) {
          e.preventDefault();
          e.stopPropagation();
          setOver(key);
        }
      },
      onDragOver: (e) => {
        if (drag?.zone === zone) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      onDrop: (e) => {
        if (drag?.zone === zone) {
          e.preventDefault();
          e.stopPropagation();
          reorder(zone, key);
        }
        setDrag(null);
        setOver(null);
      },
      'data-over': drag?.zone === zone && over === key && drag.key !== key ? '' : undefined,
      'data-dragging': drag?.key === key ? '' : undefined,
    }),
  };

  const addSection = () =>
    update((d) => {
      d.sections.push({
        key: crypto.randomUUID(),
        code: `s_${uid()}`,
        title: 'Новый раздел',
        description: '',
        questions: [newQuestion()],
        repeaters: [],
      });
    });

  return (
    <DndContext.Provider value={dnd}>
      <div className="card" style={{ padding: 16 }}>
        <div className="ed-head">
          <input
            className="title"
            value={draft.title}
            onChange={(e) => update((d) => void (d.title = e.target.value))}
          />
          <span className="vbadge">v{draft.version}</span>
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
            {draft.isActive ? 'активна' : 'архив'}
          </span>
          <button
            type="button"
            className={`sw${draft.isActive ? ' on' : ''}`}
            aria-label="активна"
            onClick={() => update((d) => void (d.isActive = !d.isActive))}
          />
        </div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>
          code: <span className="toks">{draft.code}</span> · стабильный ID, неизменяем
          (переименование = новая анкета)
        </div>

        {draft.sections.map((sec, si) => (
          <SectionBlock
            key={sec.key}
            draft={draft}
            section={sec}
            sectionIdx={si}
            update={update}
            editingKey={editingKey}
            setEditingKey={setEditingKey}
          />
        ))}

        <div className="addrow" style={{ border: '1px dashed var(--line)', borderRadius: 10 }}>
          <button type="button" style={{ flex: 1 }} onClick={addSection}>
            + Добавить раздел
          </button>
        </div>

        <div style={{ display: 'flex', gap: 10, marginTop: 16, alignItems: 'center' }}>
          <button type="button" className="btn" disabled={saving} onClick={onSave}>
            {saving ? 'Сохраняю…' : `Сохранить → v${draft.version + 1}`}
          </button>
          {saveMsg && (
            <span
              style={{
                fontSize: 13,
                color: saveMsg.startsWith('Ошибка') ? 'var(--red)' : 'var(--green)',
              }}
            >
              {saveMsg}
            </span>
          )}
        </div>
        <div className="note">
          Удаление вопроса = архивация (<span className="toks">is_active=false</span>) на сервере —
          ответы не теряются. <span className="toks">code</span> неизменяем. Порядок меняется
          перетаскиванием за <span className="drag">⠿</span> или кнопками ↑↓. Сохранение поднимает
          версию; старые ответы хранят свою формулировку (snapshot).
        </div>
      </div>
    </DndContext.Provider>
  );
}

function SectionBlock({
  draft,
  section,
  sectionIdx,
  update,
  editingKey,
  setEditingKey,
}: {
  draft: Draft;
  section: DraftSection;
  sectionIdx: number;
  update: (mut: (d: Draft) => void) => void;
  editingKey: string | null;
  setEditingKey: (k: string | null) => void;
}) {
  const dnd = useDnd();
  const numberQuestions = section.questions.filter((q) => q.type === 'number');
  const lastSection = draft.sections.length - 1;

  const addQuestion = () =>
    update((d) => {
      secAt(d, sectionIdx).questions.push(newQuestion());
    });
  const addRepeater = () =>
    update((d) => {
      const sec = secAt(d, sectionIdx);
      const count = sec.questions.find((q) => q.type === 'number');
      sec.repeaters.push({
        key: crypto.randomUUID(),
        code: `r_${uid()}`,
        title: 'Новый повтор',
        countQuestion: count?.code ?? '',
        questions: [newQuestion()],
      });
    });

  return (
    <div className="sblock">
      <div className="sh" {...dnd.zone('sections', section.key)}>
        <span className="drag" {...dnd.handle('sections', section.key)}>
          ⠿
        </span>
        <input
          value={section.title}
          onChange={(e) => update((d) => void (secAt(d, sectionIdx).title = e.target.value))}
        />
        <span className="scount">
          {section.questions.length} вопр.
          {section.repeaters.length > 0 ? ` + ${section.repeaters.length} повтор` : ''}
        </span>
        <button
          type="button"
          className="iconbtn"
          title="выше"
          disabled={sectionIdx === 0}
          onClick={() =>
            update((d) => {
              const [s] = d.sections.splice(sectionIdx, 1);
              if (s) d.sections.splice(sectionIdx - 1, 0, s);
            })
          }
        >
          ↑
        </button>
        <button
          type="button"
          className="iconbtn"
          title="ниже"
          disabled={sectionIdx === lastSection}
          onClick={() =>
            update((d) => {
              const [s] = d.sections.splice(sectionIdx, 1);
              if (s) d.sections.splice(sectionIdx + 1, 0, s);
            })
          }
        >
          ↓
        </button>
        <button
          type="button"
          className="iconbtn"
          title="удалить раздел"
          disabled={draft.sections.length === 1}
          onClick={() => {
            if (confirm(`Удалить раздел «${section.title}»? Его вопросы будут архивированы.`))
              update((d) => void d.sections.splice(sectionIdx, 1));
          }}
        >
          🗑
        </button>
      </div>

      {section.questions.map((q, qi) => (
        <QuestionItem
          key={q.key}
          draft={draft}
          sectionIdx={sectionIdx}
          path={{ kind: 'section', qi }}
          zone={`sq:${section.key}`}
          question={q}
          editing={editingKey === q.key}
          toggle={() => setEditingKey(editingKey === q.key ? null : q.key)}
          update={update}
          canMoveUp={qi > 0}
          canMoveDown={qi < section.questions.length - 1}
          canDelete={section.questions.length > 1}
        />
      ))}

      {section.repeaters.map((rep, ri) => (
        <div className="repblock" key={rep.key}>
          <div className="rh">
            🔁 Повтор{' '}
            <input
              value={rep.title}
              onChange={(e) =>
                update((d) => void (repAt(d, sectionIdx, ri).title = e.target.value))
              }
              style={{
                background: 'var(--panel)',
                border: '1px solid #3a3560',
                color: '#cbb8ff',
                borderRadius: 7,
                padding: '4px 8px',
                fontFamily: 'inherit',
                fontSize: 12,
              }}
            />
            — для каждого из:
            <select
              value={rep.countQuestion}
              onChange={(e) =>
                update((d) => void (repAt(d, sectionIdx, ri).countQuestion = e.target.value))
              }
              style={{ width: 'auto' }}
            >
              {numberQuestions.length === 0 && <option value="">— нет числового вопроса —</option>}
              {numberQuestions.map((q) => (
                <option key={q.code} value={q.code}>
                  {q.text || q.code}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="iconbtn"
              title="удалить повтор"
              onClick={() => {
                if (confirm(`Удалить повтор «${rep.title}»?`))
                  update((d) => void secAt(d, sectionIdx).repeaters.splice(ri, 1));
              }}
            >
              🗑
            </button>
          </div>
          {rep.questions.map((q, qi) => (
            <QuestionItem
              key={q.key}
              draft={draft}
              sectionIdx={sectionIdx}
              path={{ kind: 'repeater', ri, qi }}
              zone={`rq:${section.key}|${rep.key}`}
              question={q}
              editing={editingKey === q.key}
              toggle={() => setEditingKey(editingKey === q.key ? null : q.key)}
              update={update}
              canMoveUp={qi > 0}
              canMoveDown={qi < rep.questions.length - 1}
              canDelete={rep.questions.length > 1}
            />
          ))}
          <div className="addrow">
            <button
              type="button"
              onClick={() => update((d) => repAt(d, sectionIdx, ri).questions.push(newQuestion()))}
            >
              + вопрос в повтор
            </button>
          </div>
        </div>
      ))}

      <div className="addrow">
        <button type="button" onClick={addQuestion}>
          + вопрос
        </button>
        <button type="button" onClick={addRepeater} title="нужен числовой вопрос в разделе">
          + повтор (repeater)
        </button>
      </div>
    </div>
  );
}

type QPath = { kind: 'section'; qi: number } | { kind: 'repeater'; ri: number; qi: number };

function QuestionItem({
  draft,
  sectionIdx,
  path,
  zone,
  question,
  editing,
  toggle,
  update,
  canMoveUp,
  canMoveDown,
  canDelete,
}: {
  draft: Draft;
  sectionIdx: number;
  path: QPath;
  zone: string;
  question: DraftQuestion;
  editing: boolean;
  toggle: () => void;
  update: (mut: (d: Draft) => void) => void;
  canMoveUp: boolean;
  canMoveDown: boolean;
  canDelete: boolean;
}) {
  const dnd = useDnd();
  const inRepeater = path.kind === 'repeater';

  // достаём массив-владелец вопроса из черновика по пути.
  const arrOf = (d: Draft): DraftQuestion[] =>
    path.kind === 'section'
      ? secAt(d, sectionIdx).questions
      : repAt(d, sectionIdx, path.ri).questions;
  const mutQ = (mut: (q: DraftQuestion) => void) =>
    update((d) => {
      const q = arrOf(d)[path.qi];
      if (q) mut(q);
    });
  const move = (delta: number) =>
    update((d) => {
      const arr = arrOf(d);
      const [q] = arr.splice(path.qi, 1);
      if (q) arr.splice(path.qi + delta, 0, q);
    });
  const remove = () => update((d) => void arrOf(d).splice(path.qi, 1));

  const predicates = predicatesFor(draft, question.key);
  const predByCode = new Map(predicates.map((p) => [p.code, p]));

  return (
    <>
      <div className="qitem" {...dnd.zone(zone, question.key)}>
        <span className="drag" {...dnd.handle(zone, question.key)}>
          ⠿
        </span>
        <div className="qt">
          <b>{question.text || <span style={{ color: 'var(--muted)' }}>без текста</span>}</b>
          <div className="meta">
            <span className="tchip">{TYPE_LABELS[question.type]}</span>
            {question.isRequired && <span className="tchip req">обязательный</span>}
            {inRepeater && <span className="tchip rep">в повторе</span>}
            {question.conditions
              .filter((c) => c.dependsOn)
              // biome-ignore lint/suspicious/noArrayIndexKey: условия отображаются по позиции
              .map((c, i) => (
                <span className="tchip cond" key={i}>
                  если «{predByCode.get(c.dependsOn)?.text || c.dependsOn}»{' '}
                  {OP_LABELS[c.operator]} {String(c.value)}
                </span>
              ))}
          </div>
        </div>
        <div className="acts">
          <button
            type="button"
            className="iconbtn"
            title="вверх"
            disabled={!canMoveUp}
            onClick={() => move(-1)}
          >
            ↑
          </button>
          <button
            type="button"
            className="iconbtn"
            title="вниз"
            disabled={!canMoveDown}
            onClick={() => move(1)}
          >
            ↓
          </button>
          <button type="button" className="iconbtn" title="редактировать" onClick={toggle}>
            ✎
          </button>
          <button
            type="button"
            className="iconbtn"
            title="удалить (архивировать)"
            disabled={!canDelete}
            onClick={() => {
              if (confirm('Удалить вопрос? На сервере он будет архивирован, ответы сохранятся.'))
                remove();
            }}
          >
            🗑
          </button>
        </div>
      </div>

      {editing && (
        <div className="qform">
          <div className="frow">
            <div className="f1">
              <label>Текст вопроса</label>
              <input
                type="text"
                value={question.text}
                onChange={(e) => mutQ((q) => void (q.text = e.target.value))}
              />
            </div>
            <div style={{ width: 200 }}>
              <label>Тип ответа</label>
              <select
                value={question.type}
                onChange={(e) => mutQ((q) => void (q.type = e.target.value as QuestionType))}
              >
                {TYPE_ORDER.map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="frow">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <button
                type="button"
                className={`sw${question.isRequired ? ' on' : ''}`}
                aria-label="обязательный"
                onClick={() => mutQ((q) => void (q.isRequired = !q.isRequired))}
              />
              <span style={{ fontSize: 13 }}>Обязательный</span>
            </div>
          </div>

          {question.type === 'single_choice' && (
            <div style={{ marginBottom: 10 }}>
              <label>Варианты ответа</label>
              {question.options.map((opt, oi) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: опции редактируются по позиции
                <div className="opt-row" key={oi}>
                  <input
                    type="text"
                    value={opt}
                    onChange={(e) => mutQ((q) => void (q.options[oi] = e.target.value))}
                  />
                  <button
                    type="button"
                    className="iconbtn"
                    title="удалить вариант"
                    onClick={() => mutQ((q) => void q.options.splice(oi, 1))}
                  >
                    🗑
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="iconbtn"
                style={{ width: 'auto', padding: '0 10px' }}
                onClick={() => mutQ((q) => q.options.push('Вариант'))}
              >
                + вариант
              </button>
            </div>
          )}

          <ConditionsEditor
            question={question}
            predicates={predicates}
            predByCode={predByCode}
            mutQ={mutQ}
          />
        </div>
      )}
    </>
  );
}

// --- редактор условий показа (множественные, И-семантика) -------------------

function ConditionsEditor({
  question,
  predicates,
  predByCode,
  mutQ,
}: {
  question: DraftQuestion;
  predicates: Predicate[];
  predByCode: Map<string, Predicate>;
  mutQ: (mut: (q: DraftQuestion) => void) => void;
}) {
  const addCondition = () =>
    mutQ((q) => {
      const first = predicates[0];
      if (!first) return;
      q.conditions.push({
        dependsOn: first.code,
        operator: 'equals',
        value: first.type === 'yes_no' ? true : '',
      });
    });

  return (
    <>
      <label>
        Показывать, только если выполнены ВСЕ условия{' '}
        {question.conditions.length > 1 && <span style={{ color: 'var(--muted)' }}>(И)</span>}:
      </label>
      {predicates.length === 0 ? (
        <div className="cond-builder">
          <span style={{ color: 'var(--muted)', fontSize: 12 }}>
            — всегда — (нет предыдущих вопросов, от которых можно зависеть)
          </span>
        </div>
      ) : (
        <>
          {question.conditions.length === 0 && (
            <div className="cond-builder">
              <span style={{ color: 'var(--muted)', fontSize: 12 }}>
                — всегда — (условий нет; вопрос показывается каждый раз)
              </span>
            </div>
          )}
          {question.conditions.map((cond, ci) => {
            const pred = predByCode.get(cond.dependsOn);
            return (
              // biome-ignore lint/suspicious/noArrayIndexKey: условия редактируются по позиции
              <div className="cond-builder" key={ci}>
                <span style={{ color: 'var(--muted)', fontSize: 13 }}>
                  {ci === 0 ? 'если' : 'и если'}
                </span>
                <select
                  value={cond.dependsOn}
                  onChange={(e) =>
                    mutQ((q) => {
                      const c = q.conditions[ci];
                      if (!c) return;
                      c.dependsOn = e.target.value;
                      // тип предиката сменился — сбросим значение к корректному дефолту.
                      const p = predByCode.get(e.target.value);
                      c.value = p?.type === 'yes_no' ? true : '';
                      c.operator = 'equals';
                    })
                  }
                >
                  {/* предикат, на который ссылается условие, мог стать недоступен
                      (его удалили/сдвинули) — покажем его как явный пункт. */}
                  {!pred && cond.dependsOn && (
                    <option value={cond.dependsOn}>{cond.dependsOn} (недоступен)</option>
                  )}
                  {predicates.map((p) => (
                    <option key={p.code} value={p.code}>
                      {p.text || p.code}
                    </option>
                  ))}
                </select>
                <ConditionValue
                  predicate={pred}
                  condition={cond}
                  onOp={(op) =>
                    mutQ((q) => {
                      const c = q.conditions[ci];
                      if (c) c.operator = op;
                    })
                  }
                  onValue={(v) =>
                    mutQ((q) => {
                      const c = q.conditions[ci];
                      if (c) c.value = v;
                    })
                  }
                />
                <button
                  type="button"
                  className="iconbtn"
                  title="убрать условие"
                  onClick={() =>
                    mutQ((q) => {
                      q.conditions.splice(ci, 1);
                    })
                  }
                >
                  🗑
                </button>
              </div>
            );
          })}
          <button
            type="button"
            className="iconbtn"
            style={{ width: 'auto', padding: '0 10px', marginTop: 2 }}
            onClick={addCondition}
          >
            + условие
          </button>
        </>
      )}
    </>
  );
}

function ConditionValue({
  predicate,
  condition,
  onOp,
  onValue,
}: {
  predicate?: Predicate;
  condition: DraftCondition;
  onOp: (op: ConditionOperator) => void;
  onValue: (v: string | number | boolean) => void;
}) {
  return (
    <>
      <select value={condition.operator} onChange={(e) => onOp(e.target.value as ConditionOperator)}>
        <option value="equals">равно</option>
        <option value="not_equals">не равно</option>
      </select>
      {predicate?.type === 'yes_no' ? (
        <select
          value={String(condition.value)}
          onChange={(e) => onValue(e.target.value === 'true')}
        >
          <option value="true">Да</option>
          <option value="false">Нет</option>
        </select>
      ) : predicate?.type === 'number' ? (
        <input
          type="text"
          value={String(condition.value ?? '')}
          style={{ width: 90 }}
          onChange={(e) => onValue(Number(e.target.value) || 0)}
        />
      ) : (
        <input
          type="text"
          value={String(condition.value ?? '')}
          style={{ width: 160 }}
          onChange={(e) => onValue(e.target.value)}
        />
      )}
    </>
  );
}

// ============================================================
// Предпросмотр в Telegram (упрощённый)
// ============================================================

function Preview({ draft }: { draft: Draft }) {
  const firstSection = draft.sections[0];
  return (
    <div className="phone">
      <div className="pt">📱 как увидит сотрудник в Telegram</div>
      {firstSection && <div className="bubble">{firstSection.title}</div>}
      {firstSection?.questions.slice(0, 6).map((q) => (
        <div className="bubble" key={q.key}>
          {q.text || '…'}
          {q.type === 'yes_no' && (
            <div className="tgbtns">
              <span className="tgbtn">Да</span>
              <span className="tgbtn">Нет</span>
            </div>
          )}
          {q.type === 'single_choice' && q.options.length > 0 && (
            <div className="tgbtns">
              {q.options.map((o) => (
                <span className="tgbtn" key={o}>
                  {o}
                </span>
              ))}
            </div>
          )}
          {(q.type === 'number' || q.type === 'date' || q.type === 'time') && (
            <small style={{ color: 'var(--muted)', display: 'block', marginTop: 4 }}>
              ответь: {TYPE_LABELS[q.type]}
            </small>
          )}
        </div>
      ))}
      {firstSection && firstSection.questions.length > 6 && (
        <div className="bubble" style={{ color: 'var(--muted)' }}>
          … и ещё {firstSection.questions.length - 6}
        </div>
      )}
    </div>
  );
}
